/**
 * MEGA MENU COMPONENT
 * Clean, accessible, and SEO-friendly mega menu
 * Desktop (fine pointer): hover open on .nav-item--mega; mobile: click to toggle
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    activeClass: 'active',
    bodyLockClass: 'mega-menu-open',
    desktopMinWidth: 992,
    /** Dwell before opening — avoids menus opening on quick pointer passes */
    hoverOpenDelayMs: 120,
    /** Grace period after leaving nav item + panel — re-entering within this
        window cancels the close, so slipping a few px off the menu (or crossing
        the gap between trigger and panel) doesn't dismiss it */
    hoverCloseDelayMs: 300,
    selectors: {
      trigger: '[data-mega-trigger]',
      megaMenu: '.mega-menu'
    }
  };

  // State management
  let activeMegaMenu = null;
  let activeTrigger = null;
  let savedScrollY = 0;
  /** @type {'hover'|'keyboard'|'click'|null} */
  let openInteraction = null;
  let scrollLockedForActiveMenu = false;
  let hoverCloseTimer = null;
  let hoverOpenTimer = null;
  let lastViewportWasDesktop = typeof window !== 'undefined' && window.innerWidth >= CONFIG.desktopMinWidth;

  const LOCAL_VIEWPORT_GAP_PX = 16;
  const LOCAL_POSITION_GUTTER_PX = 12;
  /** Match mega-menu.css desktop transition (~0.14s) before clearing inline `left` */
  const LOCAL_MENU_LEFT_CLEAR_MS = 160;

  function clearHoverOpenTimer() {
    if (hoverOpenTimer) {
      clearTimeout(hoverOpenTimer);
      hoverOpenTimer = null;
    }
  }

  function isDesktopMega() {
    return window.innerWidth >= CONFIG.desktopMinWidth;
  }

  /**
   * Desktop hover only when viewport is wide and device supports real hover (avoids sticky menus on touch-primary tablets).
   */
  function canUseMegaHover() {
    return (
      isDesktopMega() &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
  }

  /**
   * Initialize mega menu on DOM ready
   */
  function init() {
    const triggers = document.querySelectorAll(CONFIG.selectors.trigger);

    triggers.forEach(trigger => {
      trigger.addEventListener('click', handleTriggerClick);
      trigger.addEventListener('keydown', handleTriggerKeydown);
    });

    initDesktopMegaHover();

    // Mobile close buttons
    const closeButtons = document.querySelectorAll('[data-mega-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeMegaMenu);
    });

    // Global click listener for outside clicks
    document.addEventListener('click', handleOutsideClick);

    // ESC key to close
    document.addEventListener('keydown', handleEscapeKey);

    // Close on breakpoint cross + update position
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const nowDesktop = window.innerWidth >= CONFIG.desktopMinWidth;
        if (lastViewportWasDesktop !== nowDesktop && activeMegaMenu) {
          closeMegaMenu();
        }
        lastViewportWasDesktop = nowDesktop;
        if (activeMegaMenu) {
          if (isLocalMenu(activeMegaMenu) && activeTrigger) {
            setLocalMegaViewportVars();
            positionLocalMegaMenu(activeMegaMenu, activeTrigger);
          } else {
            setMegaMenuPosition(activeMegaMenu);
          }
        }
      }, 250);
    });

    // Fix mobile navigation alignment
    handleMobileNavAlignment();
  }

  /**
   * Swap panels when moving between mega items (no close flash, no focus side effects).
   */
  function switchHoverMegaMenu(trigger, megaMenu) {
    const prevMenu = activeMegaMenu;
    const prevTrigger = activeTrigger;
    if (!prevMenu || !prevTrigger) {
      openMegaMenu(trigger, megaMenu, { interaction: 'hover' });
      return;
    }
    if (prevMenu === megaMenu) return;

    removeTrapFocus(prevMenu);

    if (prevMenu.hasAttribute('data-mega-local')) {
      scheduleClearLocalMegaMenuLeft(prevMenu);
    }

    if (scrollLockedForActiveMenu) {
      document.body.classList.remove(CONFIG.bodyLockClass);
      document.documentElement.style.removeProperty('--mega-menu-scroll-y');
      window.scrollTo(0, savedScrollY);
      scrollLockedForActiveMenu = false;
    }

    prevTrigger.setAttribute('aria-expanded', 'false');
    prevMenu.setAttribute('aria-hidden', 'true');
    prevMenu.classList.remove(CONFIG.activeClass);

    openInteraction = 'hover';
    activeMegaMenu = megaMenu;
    activeTrigger = trigger;

    cancelPendingLocalLeftClear(megaMenu);

    if (isLocalMenu(megaMenu)) {
      setMegaMenuPosition(megaMenu);
      setLocalMegaViewportVars();
      positionLocalMegaMenu(megaMenu, trigger);
    } else {
      clearLocalMegaViewportVars();
      setMegaMenuPosition(megaMenu);
    }
    trigger.setAttribute('aria-expanded', 'true');
    megaMenu.setAttribute('aria-hidden', 'false');
    megaMenu.classList.add(CONFIG.activeClass);
  }

  /**
   * Hover open/close on wrapper <li> (bridges trigger and panel)
   */
  function initDesktopMegaHover() {
    document.querySelectorAll('.nav-item--mega').forEach(li => {
      const trigger = li.querySelector(CONFIG.selectors.trigger);
      const megaMenu = li.querySelector(CONFIG.selectors.megaMenu);
      if (!trigger || !megaMenu) return;

      li.addEventListener('mouseenter', () => {
        if (!canUseMegaHover()) return;
        if (hoverCloseTimer) {
          clearTimeout(hoverCloseTimer);
          hoverCloseTimer = null;
        }

        if (activeMegaMenu === megaMenu) return;

        if (activeMegaMenu && activeMegaMenu !== megaMenu) {
          clearHoverOpenTimer();
          switchHoverMegaMenu(trigger, megaMenu);
          return;
        }

        clearHoverOpenTimer();
        hoverOpenTimer = setTimeout(() => {
          hoverOpenTimer = null;
          if (!li.matches(':hover')) return;
          if (activeMegaMenu === megaMenu) return;
          if (activeMegaMenu && activeMegaMenu !== megaMenu) {
            switchHoverMegaMenu(trigger, megaMenu);
            return;
          }
          openMegaMenu(trigger, megaMenu, { interaction: 'hover' });
        }, CONFIG.hoverOpenDelayMs);
      });

      li.addEventListener('mouseleave', () => {
        if (!canUseMegaHover()) return;
        clearHoverOpenTimer();

        if (activeMegaMenu !== megaMenu || activeTrigger !== trigger) return;

        hoverCloseTimer = setTimeout(() => {
          hoverCloseTimer = null;
          if (activeMegaMenu === megaMenu && activeTrigger === trigger) {
            closeMegaMenu();
          }
        }, CONFIG.hoverCloseDelayMs);
      });
    });
  }

  /**
   * Remove ms-auto class on mobile to fix alignment
   */
  function handleMobileNavAlignment() {
    const navbarNav = document.querySelector('.navbar-nav');
    if (!navbarNav) return;

    function updateAlignment() {
      if (window.innerWidth <= 991) {
        navbarNav.classList.remove('ms-auto');
      } else {
        navbarNav.classList.add('ms-auto');
      }
    }

    updateAlignment();
    window.addEventListener('resize', updateAlignment);
  }

  function getLocalizedInlineMobileNav(element) {
    if (window.innerWidth > 767) return null;
    if (document.querySelector('[data-localized-flat-menu]')) return null;
    const nav = element?.closest('nav[data-localized-mega-mobile]') || document.querySelector('nav[data-localized-mega-mobile]');
    return nav || null;
  }

  function setLocalizedInlineMenuState(trigger, megaMenu, isOpen) {
    if (!trigger || !megaMenu) return;
    trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    megaMenu.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
    megaMenu.classList.toggle(CONFIG.activeClass, isOpen);
  }

  function setLocalizedInlineMenusExpanded(nav, expanded) {
    if (!nav) return;
    const triggers = nav.querySelectorAll(CONFIG.selectors.trigger);
    triggers.forEach(trigger => {
      const menuId = trigger.getAttribute('aria-controls');
      const megaMenu = menuId ? document.getElementById(menuId) : null;
      if (!megaMenu) return;
      setLocalizedInlineMenuState(trigger, megaMenu, expanded);
    });
  }

  /**
   * Handle trigger button click
   */
  function handleTriggerClick(e) {
    const isLink =
      e.currentTarget.tagName === 'A' && e.currentTarget.getAttribute('href');

    // Where the panel opens on hover it is already open by the time the click
    // lands, so there is nothing to toggle - let a linked trigger navigate.
    // Everywhere else (touch, narrow viewports) the click IS how the panel
    // opens, so it must be swallowed or the tap would leave the page.
    if (canUseMegaHover()) {
      if (isLink) return;
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const trigger = e.currentTarget;
    const menuId = trigger.getAttribute('aria-controls');
    const megaMenu = document.getElementById(menuId);

    if (!megaMenu) return;

    const localizedInlineNav = getLocalizedInlineMobileNav(trigger);
    if (localizedInlineNav) {
      const isOpen = megaMenu.classList.contains(CONFIG.activeClass);
      setLocalizedInlineMenuState(trigger, megaMenu, !isOpen);
      return;
    }

    if (activeMegaMenu === megaMenu) {
      closeMegaMenu();
    } else {
      if (activeMegaMenu) {
        closeMegaMenu();
      }
      openMegaMenu(trigger, megaMenu, { interaction: 'click' });
    }
  }

  /**
   * Handle keyboard navigation for trigger
   */
  function handleTriggerKeydown(e) {
    const trigger = e.currentTarget;
    const menuId = trigger.getAttribute('aria-controls');
    const megaMenu = document.getElementById(menuId);

    if (e.key === 'Enter' || e.key === ' ') {
      // Enter on a linked trigger follows the link, the same as a click.
      // ArrowDown below is how the keyboard opens the panel.
      if (
        e.key === 'Enter' &&
        trigger.tagName === 'A' &&
        trigger.getAttribute('href') &&
        canUseMegaHover()
      ) {
        return;
      }
      e.preventDefault();
      if (canUseMegaHover()) {
        if (!megaMenu) return;
        if (activeMegaMenu === megaMenu) {
          closeMegaMenu();
        } else {
          if (activeMegaMenu) closeMegaMenu();
          openMegaMenu(trigger, megaMenu, { interaction: 'keyboard' });
        }
      } else {
        handleTriggerClick(e);
      }
    }

    if (e.key === 'ArrowDown' && megaMenu) {
      e.preventDefault();
      if (activeMegaMenu !== megaMenu) {
        openMegaMenu(trigger, megaMenu, { interaction: 'keyboard' });
      }
      focusFirstLink(megaMenu);
    }
  }

  /**
   * Returns true when a menu should behave as a local (anchored) dropdown.
   * Only applies on desktop — mobile always uses the full-screen behaviour.
   */
  function isLocalMenu(megaMenu) {
    return megaMenu.hasAttribute('data-mega-local') && isDesktopMega();
  }

  function getHeaderBottom() {
    const el =
      document.querySelector('nav.navbar .container-fluid') ||
      document.querySelector('nav.navbar');
    if (!el) return 0;
    return Math.ceil(el.getBoundingClientRect().bottom);
  }

  function setLocalMegaViewportVars() {
    const headerBottom = getHeaderBottom();
    const maxH = Math.max(
      200,
      window.innerHeight - headerBottom - LOCAL_VIEWPORT_GAP_PX
    );
    document.documentElement.style.setProperty(
      '--mega-menu-header-bottom',
      headerBottom + 'px'
    );
    document.documentElement.style.setProperty(
      '--mega-local-panel-max-height',
      maxH + 'px'
    );
  }

  function clearLocalMegaViewportVars() {
    document.documentElement.style.removeProperty('--mega-menu-header-bottom');
    document.documentElement.style.removeProperty('--mega-local-panel-max-height');
  }

  function cancelPendingLocalLeftClear(megaMenu) {
    if (!megaMenu || !megaMenu._megaLocalLeftClearTimer) return;
    clearTimeout(megaMenu._megaLocalLeftClearTimer);
    megaMenu._megaLocalLeftClearTimer = null;
  }

  /**
   * Defer clearing inline `left` until after the close transition so the panel
   * doesn’t snap to CSS `left: 0` while fading (horizontal jump / “flies off”).
   */
  function scheduleClearLocalMegaMenuLeft(megaMenu) {
    if (!megaMenu || !megaMenu.hasAttribute('data-mega-local')) return;
    cancelPendingLocalLeftClear(megaMenu);
    megaMenu._megaLocalLeftClearTimer = setTimeout(() => {
      megaMenu._megaLocalLeftClearTimer = null;
      if (!megaMenu.classList.contains(CONFIG.activeClass)) {
        megaMenu.style.removeProperty('left');
      }
    }, LOCAL_MENU_LEFT_CLEAR_MS);
  }

  function clearLocalMegaMenuPosition(megaMenu) {
    if (!megaMenu) return;
    cancelPendingLocalLeftClear(megaMenu);
    megaMenu.style.removeProperty('left');
  }

  /**
   * Horizontally clamp the local panel under its nav item so it stays in the viewport.
   * @param {HTMLElement} megaMenu
   * @param {HTMLElement} trigger
   */
  function positionLocalMegaMenu(megaMenu, trigger) {
    if (!megaMenu || !trigger || !isLocalMenu(megaMenu)) return;
    const navItem = trigger.closest('.nav-item--mega');
    if (!navItem) return;

    function measureAndApply() {
      if (!isLocalMenu(megaMenu) || activeMegaMenu !== megaMenu) return;
      const liRect = navItem.getBoundingClientRect();
      const vw = window.innerWidth;

      // Measure the white box people actually see, not the wrapper. Several
      // panels are content-sized now - `.mega-menu__inner` shrinks to its
      // columns while `.mega-menu` around it stays 1200px - so measuring the
      // wrapper treated a 500px Los Angeles panel as 1200px and shoved it a
      // full panel-width to the left of the label that opened it.
      const inner = megaMenu.querySelector('.mega-menu__inner');
      const menuRect = megaMenu.getBoundingClientRect();
      const boxRect = inner ? inner.getBoundingClientRect() : menuRect;
      const panelWidth = boxRect.width;
      // How far the visible box sits inside the wrapper. Stable while the
      // wrapper is translated horizontally, so it can be solved for directly.
      const boxOffset = boxRect.left - menuRect.left;

      // Centre the box on its nav item, then keep it inside the viewport -
      // shifted only as far as the overflow requires. Centring is only safe
      // now that panelWidth is the visible box: measuring the 1200px wrapper
      // used to centre a 491px panel as if it were 1200 and throw it far left.
      const idealLeft = (liRect.width - panelWidth) / 2 - boxOffset;
      let leftMin = LOCAL_POSITION_GUTTER_PX - liRect.left - boxOffset;
      let leftMax =
        vw - LOCAL_POSITION_GUTTER_PX - panelWidth - liRect.left - boxOffset;
      if (leftMin > leftMax) {
        leftMin = leftMax = LOCAL_POSITION_GUTTER_PX - liRect.left - boxOffset;
      }
      const left = Math.min(Math.max(idealLeft, leftMin), leftMax);
      megaMenu.style.left = Math.round(left) + 'px';
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(measureAndApply);
    });
    // A content-sized panel is only as wide as its text, so its width is not
    // final until the webfont has swapped in. Re-measure once when it does.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureAndApply);
    }
  }

  /**
   * Calculate and set mega menu position based on navbar height.
   * Skipped for local dropdowns on desktop (CSS handles positioning).
   */
  function setMegaMenuPosition(megaMenu) {
    if (isLocalMenu(megaMenu)) return;

    const navbar = document.querySelector('.navbar');
    if (!navbar) return;

    const navbarHeight = navbar.offsetHeight;
    megaMenu.style.top = navbarHeight + 'px';
  }

  /**
   * @param {HTMLElement} trigger
   * @param {HTMLElement} megaMenu
   * @param {{ interaction?: 'hover'|'keyboard'|'click' }} [options]
   */
  function openMegaMenu(trigger, megaMenu, options) {
    options = options || {};
    const interaction = options.interaction || 'click';
    openInteraction = interaction;

    activeMegaMenu = megaMenu;
    activeTrigger = trigger;

    cancelPendingLocalLeftClear(megaMenu);

    setMegaMenuPosition(megaMenu);

    if (isLocalMenu(megaMenu)) {
      setLocalMegaViewportVars();
      positionLocalMegaMenu(megaMenu, trigger);
    } else {
      clearLocalMegaViewportVars();
    }

    trigger.setAttribute('aria-expanded', 'true');
    megaMenu.setAttribute('aria-hidden', 'false');

    megaMenu.classList.add(CONFIG.activeClass);

    scrollLockedForActiveMenu = false;
    const skipBodyLock = interaction === 'hover' || isLocalMenu(megaMenu);
    if (!skipBodyLock) {
      savedScrollY = window.scrollY;
      document.documentElement.style.setProperty('--mega-menu-scroll-y', savedScrollY + 'px');
      document.body.classList.add(CONFIG.bodyLockClass);
      scrollLockedForActiveMenu = true;
    }

    if (interaction !== 'hover') {
      trapFocus(megaMenu);
    }
  }

  function removeTrapFocus(megaMenu) {
    if (!megaMenu || !megaMenu._trapFocusHandler) return;
    megaMenu.removeEventListener('keydown', megaMenu._trapFocusHandler);
    megaMenu._trapFocusHandler = null;
  }

  /**
   * Close mega menu
   */
  function closeMegaMenu() {
    if (!activeMegaMenu || !activeTrigger) return;

    clearHoverOpenTimer();
    if (hoverCloseTimer) {
      clearTimeout(hoverCloseTimer);
      hoverCloseTimer = null;
    }

    const megaMenu = activeMegaMenu;
    const trigger = activeTrigger;
    const interaction = openInteraction;
    const focusInside = megaMenu.contains(document.activeElement);
    const shouldReturnFocus =
      trigger && (interaction !== 'hover' || focusInside);

    removeTrapFocus(megaMenu);

    trigger.setAttribute('aria-expanded', 'false');
    megaMenu.setAttribute('aria-hidden', 'true');

    megaMenu.classList.remove(CONFIG.activeClass);

    clearLocalMegaViewportVars();
    if (megaMenu.hasAttribute('data-mega-local')) {
      scheduleClearLocalMegaMenuLeft(megaMenu);
    }

    if (scrollLockedForActiveMenu) {
      document.body.classList.remove(CONFIG.bodyLockClass);
      document.documentElement.style.removeProperty('--mega-menu-scroll-y');
      window.scrollTo(0, savedScrollY);
    }
    scrollLockedForActiveMenu = false;

    if (shouldReturnFocus) {
      trigger.focus();
    }

    openInteraction = null;
    activeMegaMenu = null;
    activeTrigger = null;
  }

  /**
   * Handle clicks outside mega menu
   */
  function handleOutsideClick(e) {
    if (!activeMegaMenu) return;

    const trigger = activeTrigger;
    const megaMenu = activeMegaMenu;

    if (!trigger.contains(e.target) && !megaMenu.contains(e.target)) {
      closeMegaMenu();
    }
  }

  /**
   * Handle ESC key to close mega menu
   */
  function handleEscapeKey(e) {
    if (e.key === 'Escape' && activeMegaMenu) {
      closeMegaMenu();
    }
  }

  /**
   * Focus first link in mega menu
   */
  function focusFirstLink(megaMenu) {
    const firstLink = megaMenu.querySelector('.mega-menu__link');
    if (firstLink) {
      firstLink.focus();
    }
  }

  /**
   * Trap focus within mega menu for accessibility
   */
  function trapFocus(megaMenu) {
    removeTrapFocus(megaMenu);

    const focusableElements = megaMenu.querySelectorAll(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    function handleTabKey(e) {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }

    megaMenu.addEventListener('keydown', handleTabKey);
    megaMenu._trapFocusHandler = handleTabKey;
  }

  /**
   * Clean up and close mega menu when mobile nav is toggled
   */
  function handleMobileNavToggle() {
    const navCollapse = document.querySelector('#navbarSupportedContent');

    if (navCollapse) {
      const localizedInlineNav = navCollapse.closest('nav[data-localized-mega-mobile]');
      const hasFlatMenu = !!navCollapse.querySelector('[data-localized-flat-menu]');

      navCollapse.addEventListener('show.bs.collapse', () => {
        document.body.classList.add('nav-open');
        if (localizedInlineNav && !hasFlatMenu) {
          setLocalizedInlineMenusExpanded(localizedInlineNav, true);
        }
      });

      navCollapse.addEventListener('hide.bs.collapse', () => {
        document.body.classList.remove('nav-open');
        if (activeMegaMenu) {
          closeMegaMenu();
        }
        if (localizedInlineNav && !hasFlatMenu) {
          setLocalizedInlineMenusExpanded(localizedInlineNav, false);
        }
      });
      return;
    }

    const mobileMenuDetails = document.querySelector('#Details-menu-drawer-container');
    if (mobileMenuDetails) {
      mobileMenuDetails.addEventListener('toggle', () => {
        if (mobileMenuDetails.open) {
          document.body.classList.add('nav-open');
        } else {
          document.body.classList.remove('nav-open');
          if (activeMegaMenu) {
            closeMegaMenu();
          }
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
    document.addEventListener('DOMContentLoaded', handleMobileNavToggle);
  } else {
    init();
    handleMobileNavToggle();
  }

  window.closeMegaMenu = closeMegaMenu;

})();


/* Workshops mega menu (localised headers): show the photo of whichever class
   the pointer or keyboard focus is on. The new image is loaded off-DOM first so
   the panel never flashes an empty frame, and nothing is fetched until the
   visitor actually hovers a class. */
(function () {
  'use strict';

  function wire(root) {
    var img = root.querySelector('.ws-hover__img');
    var label = root.querySelector('.ws-hover__label');
    var links = root.querySelectorAll('a[data-ws-img]');
    if (!img || !links.length) return;

    var initialSrc = img.getAttribute('src');
    var wanted = initialSrc;

    function show(src, text) {
      if (!src || src === wanted) return;
      wanted = src;
      if (label) label.textContent = text || '';
      var pre = new Image();
      pre.onload = function () {
        if (wanted === src) img.setAttribute('src', src);
      };
      pre.src = src;
    }

    function reset() {
      wanted = initialSrc;
      if (label) label.textContent = '';
      img.setAttribute('src', initialSrc);
    }

    Array.prototype.forEach.call(links, function (a) {
      var src = a.getAttribute('data-ws-img');
      var nameEl = a.querySelector('[itemprop="name"]');
      var text = nameEl ? nameEl.textContent.trim() : '';
      function on() { show(src, text); }
      a.addEventListener('mouseenter', on);
      a.addEventListener('focus', on);
    });

    // "All Classes in <city>" and anything else without a photo of its own goes
    // back to the city default rather than leaving the last class on screen.
    var plain = root.querySelectorAll('a.mega-menu__link:not([data-ws-img])');
    Array.prototype.forEach.call(plain, function (a) {
      a.addEventListener('mouseenter', reset);
      a.addEventListener('focus', reset);
    });

    root.addEventListener('mouseleave', reset);
    root.addEventListener('focusout', function (e) {
      if (!root.contains(e.relatedTarget)) reset();
    });
  }

  function init() {
    var roots = document.querySelectorAll('.mega-menu--originals .ws-hover');
    Array.prototype.forEach.call(roots, wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
